/**
 * Feedback Triage — the page's vocabulary: statuses, colours, and the two formatters.
 *
 * The colours are not decorative: priority reuses the channel's own scale (Slack posts LOW as a
 * white circle, MEDIUM blue, HIGH orange, CRITICAL red), so someone who reads the channel every
 * day recognises a row without learning a second legend.
 */
import type {
  FeedbackReport,
  OwnerId,
  PiaPhase,
  TriageStatus,
} from '../../services/feedbackTriageApi';

export const RADIUS = 'rounded-md';

/** The statuses a person picks, in the order work actually moves through them. */
export const STATUS_OPTIONS: { value: TriageStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'triaged', label: 'Triaged' },
  { value: 'progress', label: 'In progress' },
  { value: 'waiting', label: 'Needs decision' },
  { value: 'done', label: 'Fixed' },
  { value: 'noise', label: 'Not a report' },
];

/**
 * The two statuses that take a report OFF this page. Both tick the Slack post, so the report is
 * closed everywhere — `noise` is the escape hatch for the two "Test" posts and anything else that
 * was never a report, and the channel has no other way to say "handled".
 *
 * ⚠️ This list decides whether the row is dropped optimistically; the PROXY decides whether the
 * checkmark is actually written (`CLOSING_STATUSES` in `proxy-server/lib/feedbackReports.js`,
 * which is the authority). The two disagreeing means a row that vanishes and then comes back.
 */
export const CLOSING_STATUSES: TriageStatus[] = ['done', 'noise'];

export const STATUS_LABELS: Record<TriageStatus, string> = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label])
) as Record<TriageStatus, string>;

export const PRIORITY_CHIP: Record<FeedbackReport['priority'], string> = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-blue-100 text-blue-800',
  low: 'bg-zinc-100 text-zinc-600',
};

export const PRIORITY_LABEL: Record<FeedbackReport['priority'], string> = {
  critical: 'CRIT',
  high: 'HIGH',
  medium: 'MED',
  low: 'LOW',
};

export const TYPE_CHIP: Record<FeedbackReport['type'], string> = {
  bug: 'bg-red-50 text-red-700',
  improvement: 'bg-indigo-50 text-indigo-700',
  feature: 'bg-violet-50 text-violet-700',
  other: 'bg-zinc-100 text-zinc-600',
};

/**
 * Pia's phase as a chip. Green is a fix waiting for a person, amber is Pia waiting for one, red
 * is something that needs looking at, grey is over. Not the traffic-light meaning the TA Cockpit
 * gives these hues — this page has no other colour system to clash with.
 */
export const PIA_CHIP: Record<PiaPhase, string> = {
  running: 'bg-sky-100 text-sky-800',
  waiting: 'bg-amber-100 text-amber-800',
  'pr-open': 'bg-emerald-100 text-emerald-800',
  'merge-pending': 'bg-emerald-100 text-emerald-800',
  merged: 'bg-emerald-600 text-white',
  'merge-blocked': 'bg-red-100 text-red-800',
  'pr-closed': 'bg-amber-100 text-amber-800',
  'needs-discussion': 'bg-amber-100 text-amber-800',
  'not-a-bug': 'bg-zinc-100 text-zinc-600',
  failed: 'bg-red-100 text-red-800',
  capped: 'bg-amber-100 text-amber-800',
  stopped: 'bg-zinc-100 text-zinc-600',
  'dispatch-failed': 'bg-red-100 text-red-800',
  idle: 'bg-zinc-100 text-zinc-600',
};

/**
 * Phases in which Pia herself is on the move: the "With Pia" tab. Only these two — every other
 * phase waits on a PERSON (a decision, an answer to her question, an approval of her PR, a blocked
 * merge, the round cap), so the report sits with its owner and the row's Pia chip says why
 * (Sofia, Oct 12 2026: three "Needs a decision" reports showed under "With Pia" while Theresa owned
 * them).
 */
export const ACTIVE_PIA_PHASES: PiaPhase[] = ['running', 'merge-pending'];

/** One colour per owner, used by the avatar and nowhere else. */
export const OWNER_COLOR: Record<OwnerId, string> = {
  theresa: 'bg-blue-600',
  anna: 'bg-violet-600',
  sofia: 'bg-teal-700',
  // amber-700 rather than amber-600: the initials are white, and 600 does not clear 4.5:1.
  david: 'bg-amber-700',
};

/**
 * How long a report has been sitting there, in the coarsest unit that is still true.
 *
 * Age is the only number on this page that shames anyone, and it should: the oldest open report
 * in the channel had been waiting forty days when this page was built.
 */
export function ageOf(postedAt: string): string {
  const days = Math.floor((Date.now() - new Date(postedAt).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day';
  if (days < 31) return `${days} days`;
  const months = Math.floor(days / 30);
  return months === 1 ? '1 month' : `${months} months`;
}

/** The reporter's first name and surname initial — the full address is noise in a table. */
export function reporterName(email: string): string {
  const [local] = `${email}`.split('@');
  if (!local) return 'unknown';
  const [first, last] = local.split('.');
  return last ? `${cap(first)} ${cap(last).charAt(0)}.` : cap(first);
}

function cap(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
}
